import { graphql } from "gatsby";
import clsx from "clsx";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-material-ui";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes from "prop-types";
import React, { ReactElement, useRef, useState } from "react";
import DateFnsUtils from "@date-io/dayjs";
import { Link } from "gatsby";
import {
  Container,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Theme,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import "../pages/new-developments/index.scss"
import Helmet from "react-helmet";
import PreviewCompatibleImage from "../components/PreviewCompatibleImage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content",
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
      alignItems: "center",
      gridAutoFlow: "row",
      // [theme.breakpoints.down("sm")]: {
      //   gap: `${theme.spacing(4)}px`
      // },
      [theme.breakpoints.up("sm")]: {
        alignContent: "center",
        alignItems: "center",
        justifyContent: "center",
        display: "grid",
        gap: `${theme.spacing(8)}px`,
        gridAutoFlow: "row",
        gridTemplateRows: "1fr",
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(4),
      },
    },
    label: {
      '& a ' : {
        textDecoration: "underline",
        fontWeight: 500,
      }
    },
    form: {
      display: "grid",
      justifySelf: "center",
      minWidth: '80%',
      gap: `${theme.spacing(2)}px`,
      paddingBottom: theme.spacing(4),
      paddingTop: theme.spacing(4),
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    overview: {
      alignContent: "center",
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
      display: "grid",
      padding: theme.spacing(8),
    },
    segment: {
      display: "grid",
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    description: {},
  })
);

function encode(data): string {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

export const NewDevelopmentsPageTemplate = ({
  title,
  heading,
  featuredimage,
  imagewidth,
  subheading,
  description,
  formTitle,
  content,
  contentComponent,
}): ReactElement => {
  const classes = useStyles();
  const PageContent = contentComponent || Content;

  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  const handleCheckChange = (event) => {
    setChecked(!checked)
  };
  const label = (
    <p className={classes.label}>
    I have read and agree with Williams Lynch <Link to="/privacy-policy">GPDR Policy*</Link>
    </p>
  )
  return (
    <Container className={clsx(classes.root)} component="main" fluid="fluid">
      <header>
        {/* <Typography component="h1" variant="h4" className={classes.title}>
          {title}
        </Typography> */}
        {heading && (
          <Typography
            className={clsx(classes.heading)}
            component="h1"
            variant="h2"
          >
            {heading}
          </Typography>
        )}
        {featuredimage ? (
          <PreviewCompatibleImage
            imageInfo={{
              image: featuredimage,
              alt: heading,
              style: {
                width: `${imagewidth}px`,
                minWidth: '370px',
                maxWidth: '1024px',
                margin: "1rem auto",
              }
            }}
          />
        ) : null}
        {subheading && (
          <Typography
            className={clsx(classes.subheading)}
            component="h2"
            variant="h3"
          >
            {subheading}
          </Typography>
        )}
        {description && (
          <Typography className={clsx(classes.description)} component="p">
            {description}
          </Typography>
        )}
      </header>
      <PageContent content={content} />
      <section className={clsx(classes.segment)}>

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Formik
            initialValues={{
              email: "",
              name: "",
              telefone: "",
            }}
            validate={(values): Partial => {
              const errors: Partial<Values> = {};

              if (!values.email) {
                errors.email = "Required";
              } else if (
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
              ) {
                errors.email = "Invalid email address";
              }

              return errors;
            }}
            onSubmit={(values): void => {
              const form = formRef.current;
              setIsSubmitting(true);
              fetch(form.getAttribute("action"), {
                method: "POST",
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
                body: encode({
                  "form-name": form.getAttribute("name"),
                  'subject': "ONLINE ENQUIRY – New Developments Form",
                  ...values,
                }),
              })
                .then(() => setIsSubmitting(false))
                .then(() => navigate(form.getAttribute("action")))
                .catch((error) => alert(error));
            }}
          >
            {({ submitForm, isSubmitting }): ReactElement => (
              <>
                <Typography component="legend" variant="h5">
                  {formTitle}
                </Typography>
                <Form
                  action=""
                  className={clsx(classes.form)}
                  data-netlify-honeypot="bot-field"
                  data-netlify="true"
                  method="post"
                  name="new-developments"
                  ref={formRef}
                >
                  {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
                  <input type="hidden" name="form-name" value="contact" />
                  <input type="hidden" name="subject" value="ONLINE ENQUIRY – New Developments Form"/>
                  <Field
                    component={TextField}
                    label="Name"
                    name="name"
                    type="name"
                    required
                  />

                  <Field
                    component={TextField}
                    label="Telephone"
                    name="telephone"
                    type="tel"
                    pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  />

                  <Field
                    component={TextField}
                    label="Email"
                    name="email"
                    type="email"
                    required
                  />
                  
                  <FormControlLabel
                      value="GPDR"
                      control={
                        <Checkbox
                          name="GPDR"
                          checked={checked}
                          onChange={handleCheckChange}
                          color="secondary"
                        />
                      }
                      labelPlacement="end"
                      label={label}
                  />
                  {isSubmitting && <LinearProgress />}

                  <Button
                    variant="contained"
                    disabled={isSubmitting || checked !== true}
                    onClick={submitForm}
                  >
                    Send
                </Button>
                </Form>
              </>
            )}
          </Formik>
        </MuiPickersUtilsProvider>
      </section>
    </Container>
  );
};

NewDevelopmentsPageTemplate.propTypes = {
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  formTitle: PropTypes.string.isRequired,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object
};

const NewDevelopmentsPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <NewDevelopmentsPageTemplate
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        contentComponent={HTMLContent}
        content={post.html}
        formTitle={post.frontmatter.formTitle}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

NewDevelopmentsPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export const pageQuery = graphql`
  query NewDevelopmentsPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
        formTitle
      }
    }
  }
`;

export default NewDevelopmentsPage;
